import clsx from 'clsx';
import _ from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CancelSaveAndContinue } from '@/core/CancelSaveAndContinue.molecule';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { sqExploreFolderModalStore, sqHomeScreenStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { ExplorerModalBody } from '@/explorer/ExplorerModalBody.molecule';
import * as sqExploreFolderModalActions from '@/explorer/exploreFolderModal.actions';
import { getParentFolderId } from '@/explorer/exploreFolderModal.actions';
import {
  addExpandedFolderIds,
  clearItemsHomescreen,
  loadFolder,
  loadFolderTree,
  setCurrentFolder,
  setCurrentTab,
} from '@/homescreen/homescreen.actions';
import { HOME_SCREEN_TABS, SEARCH_ITEM_LOCATIONS } from '@/main/app.constants';
import { getFolderLink } from '@/main/routing.utilities';
import { canModifyWorkbook } from '@/services/authorization.service';
import {
  fireMoveItemToast,
  getTabFolder,
  getTabFolderName,
  moveItem,
  translateBreadcrumb,
} from '@/utilities/homescreen.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { equalsIgnoreCase, truncate } from '@/utilities/utilities';
import { Modal } from '@seeqdev/qomponents';
import type { SelectedSearchItem } from '@/search/search.types';

interface FolderExplorerModalProps {
  corporateDriveMessage?: boolean;
  isAclModalLocationInHeader?: boolean;
  onClose: () => void;
  item: any;
  fetchInherited?: any;
  itemId?: string;
  itemName?: string;
  currentTab: string;
  aclModified?: any;
  onSelect?: (item: SelectedSearchItem) => void;
  onSave?: () => void;
  title?: string;
  submitLabel?: string;
  inFormComponent?: boolean;
}

export const FolderExplorerModal = (props: FolderExplorerModalProps) => {
  const {
    currentTab,
    item,
    onClose,
    fetchInherited,
    corporateDriveMessage,
    itemId,
    itemName,
    aclModified,
    isAclModalLocationInHeader,
    onSelect,
    onSave,
    title,
    submitLabel,
    inFormComponent = false,
  } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const folder = useFluxPath(sqExploreFolderModalStore, (store) => store.folder);
  const initialFolderId = useFluxPath(sqExploreFolderModalStore, (store) => store.initialFolderId);
  const localizedFolder = translateBreadcrumb(folder);
  const currentFolderId = useFluxPath(sqHomeScreenStore, (store) => store.currentFolderId);
  const tableFilter = useFluxPath(sqExploreFolderModalStore, (store) => store.tableFilter);

  useEffect(() => {
    if (item && inFormComponent) {
      getParentFolderId(currentTab, item);
    } else {
      sqExploreFolderModalActions.getInitialFolder({
        currentTab: corporateDriveMessage ? HOME_SCREEN_TABS.CORPORATE : currentTab,
        item,
        forceCorporate: corporateDriveMessage,
      });
    }
  }, []);

  const close = (movedItem = false) => {
    if (movedItem) {
      clearItemsHomescreen();
    }
    sqExploreFolderModalActions.clear();
    setCurrentTab(currentTab);
    onClose();
  };

  const updateMovedItems = () => {
    return fireMoveItemToast(corporateDriveMessage ? { name: itemName } : item, folder!);
  };

  const moveItemToFolder = () => {
    const currentFolderId = folder ? folder.id : SEARCH_ITEM_LOCATIONS.MY_FOLDER;
    return moveItem(corporateDriveMessage ? itemId : item.id, currentFolderId)
      .then((response: any) => {
        let folderId: string | null;
        let currentTab: string | null;
        let tabFolderPromise;
        if (!folder) {
          tabFolderPromise = getTabFolder(HOME_SCREEN_TABS.MY_FOLDER);
        } else {
          tabFolderPromise = getTabFolderName(currentFolderId).then((tabFolder) => {
            folderId = tabFolder ? null : currentFolderId;
            currentTab = tabFolder || null;
          });
        }
        return tabFolderPromise
          .then(() => {
            if (item !== undefined && !corporateDriveMessage) {
              return loadFolderTree(folderId, currentTab).then(() => {
                navigate(getFolderLink(currentFolderId), { replace: true });
                requestAnimationFrame(updateMovedItems);
                onClose();
              });
            } else {
              setCurrentFolder(currentFolderId);
              setCurrentTab(currentTab ?? '');
              updateMovedItems();
              fetchInherited();
              onClose();
              if (!isAclModalLocationInHeader && !aclModified) {
                return loadFolderTree(folderId, currentTab).then(() =>
                  navigate(getFolderLink(currentFolderId), { replace: true }),
                );
              } else {
                return loadFolder(folderId, currentTab ?? undefined);
              }
            }
          })
          .then(() => addExpandedFolderIds(_.map(sqHomeScreenStore.breadcrumbs, 'id')));
      })
      .catch((error) => {
        errorToast({ httpResponseOrError: error });
      });
  };

  const onSelectFolder = () => {
    if (onSelect) {
      const currentFolderId = folder ? folder.id : SEARCH_ITEM_LOCATIONS.MY_FOLDER;
      onSelect({ id: currentFolderId, name: folder?.name ?? '' } as SelectedSearchItem);
      onSave?.();
      close();
    } else {
      moveItemToFolder();
    }
  };

  const isNotCurrentFolder = !folder || folder?.id !== currentFolderId;
  const isNotFilteringByPublicOrPinned =
    tableFilter !== SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC && tableFilter !== SEARCH_ITEM_LOCATIONS.PINNED;
  const isNotFilteringByAll = tableFilter !== SEARCH_ITEM_LOCATIONS.ALL;
  const isFolderValidForMove =
    canModifyWorkbook(folder, false) &&
    folder &&
    !equalsIgnoreCase(folder?.id, initialFolderId!) &&
    !equalsIgnoreCase(folder?.id, item?.id) &&
    _.isUndefined(
      _.find(folder?.ancestors, (ancestor) => {
        return equalsIgnoreCase(ancestor.id, item?.id);
      }),
    );

  // If folder is undefined it could be that we are trying to move to the "My Folder" that has yet to be created
  const canSubmit = (): boolean => {
    let canMoveItem: boolean;
    if (corporateDriveMessage) {
      canMoveItem = isNotCurrentFolder && isNotFilteringByPublicOrPinned;
    } else if (!folder && isNotFilteringByPublicOrPinned) {
      canMoveItem = onSelect ? false : isNotFilteringByAll;
    } else {
      canMoveItem = Boolean(isFolderValidForMove);
    }
    const canMoveItemInFormComponent = Boolean(
      (inFormComponent || Boolean(onSelect)) &&
      isNotFilteringByAll &&
      isNotFilteringByPublicOrPinned &&
      canModifyWorkbook(folder, false),
    );
    return canMoveItem || canMoveItemInFormComponent;
  };

  const isSubmitDisabled = !canSubmit();

  const renderButtons = (
    <div className="flexColumnContainer flexJustifyEnd pt15">
      <CancelSaveAndContinue
        cancelFn={() => close()}
        cancelBtnTestId="cancelMoveBtn"
        submitFn={onSelectFolder}
        submitBtnTestId="moveToBtn"
        submitBtnLabel={submitLabel ?? 'WORKBENCH.MOVE_HERE'}
        btnDisabled={isSubmitDisabled}
        submitClassNames={clsx({
          'disabledLook disabledBehavior': isSubmitDisabled,
        })}
        values={[]}
      />
    </div>
  );

  return (
    <Modal
      open={true}
      onClose={close}
      testId="folderExplorerModal"
      title=""
      customHeader={
        <h3 className="modal-title flexColumnContainer flexAlignCenter textAlignLeft">
          <HoverTooltip text={localizedFolder || ''} delay={500}>
            <span data-testid="folderExplorerTitle">
              {title ? t(title) : t('WORKBENCH.MOVE_TO_FOLDER')}: {truncate(localizedFolder || '', 50, 20)}
            </span>
          </HoverTooltip>
        </h3>
      }
      modalFooter={renderButtons}>
      <div className=" max-height-475 overflowAuto">
        <ExplorerModalBody
          show={true}
          store={sqExploreFolderModalStore}
          actions={sqExploreFolderModalActions}
          allowedTypes={['Folder']}
        />
      </div>
    </Modal>
  );
};
